import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsTooltip } from '@/core/constants/webComponents';
import {
  SENTIMENT_OPTIONS,
  SENTIMENT_ICONS,
  SENTIMENT_COLORS,
  type Sentiment,
} from '@/core/constants/domainMappings';

export interface SentimentSelectorProps {
  selection: Set<Sentiment>;
  onChange: React.Dispatch<React.SetStateAction<Set<Sentiment>>>;
}

export const SentimentSelector: React.FC<SentimentSelectorProps> = ({
  selection,
  onChange,
}) => {
  const { t } = useTranslation();

  const SENTIMENT_LABELS: Record<Exclude<Sentiment, null>, string> = {
    Negativo: t('classificationCard.sentimentNegative'),
    Neutro: t('classificationCard.sentimentNeutral'),
    Positivo: t('classificationCard.sentimentPositive'),
  };

  const getSentimentBorderRadius = (option: Sentiment): string | undefined => {
    if (option === 'Negativo') return '8px 0 0 8px';
    if (option === 'Neutro') return '0';
    return undefined;
  };

  const getSentimentBtnModifier = (option: Sentiment): string => {
    if (option === 'Negativo') return 'negative';
    if (option === 'Positivo') return 'positive';
    return 'neutral';
  };

  const handleToggle = (option: Sentiment) => {
    onChange((prev) => {
      const next = new Set(prev);
      if (next.has(option)) next.delete(option);
      else next.add(option);
      return next;
    });
  };

  return (
    <div className="classification-content__sentiment">
      <span className="classification-content__sentiment-label">
        {t('multipleEdition.sentiment', 'Sentimento')}
      </span>
      <div className="classification-content__sentiment-buttons">
        {SENTIMENT_OPTIONS.map((option) => (
          <BdsTooltip
            key={option}
            position="top-right"
            tooltipText={
              selection.has(option)
                ? t('classificationCard.tooltipSentimentMarked')
                : t('globalFilter.markSentimentTooltip', { sentiment: SENTIMENT_LABELS[option] })
            }
          >
            <button
              type="button"
              className={`classification-content__sentiment-btn classification-content__sentiment-btn--${option}${
                selection.has(option) ? ' classification-content__sentiment-btn--active' : ''
              } btn-${getSentimentBtnModifier(option)}`}
              style={{ borderRadius: getSentimentBorderRadius(option) }}
              onClick={() => handleToggle(option)}
            >
              <BdsIcon name={SENTIMENT_ICONS[option]} size="medium" color={SENTIMENT_COLORS[option]} />
            </button>
          </BdsTooltip>
        ))}
      </div>
    </div>
  );
};

export function useSentimentSelection() {
  return useState<Set<Sentiment>>(new Set());
}
